import { useContext, useState } from 'react'
import { Dialog, DialogFooter, DialogType, FontIcon, PrimaryButton, Stack, TextField } from '@fluentui/react'
import { SendRegular } from '@fluentui/react-icons'

import DOCx from '../../assets/docx.svg'
import TXT from '../../assets/txt.svg'
import Send from '../../assets/Send.svg'
import Submit from '../../assets/Submit.png'

import styles from './QuestionInput.module.css'
import { ChatMessage } from '../../api'
import { AppStateContext } from '../../state/AppProvider'
import { resizeImage } from '../../utils/resizeImage'

interface Props {
  onSend: ({ question, context, id }: { question: ChatMessage['content'], context?: string, id?: string }) => void;
  disabled: boolean
  messagesLength: number; // Add messagesLength as a prop
  placeholder?: string
  clearOnSend?: boolean
  conversationId?: string
}

export const QuestionInput = ({ onSend, disabled, messagesLength, placeholder, clearOnSend, conversationId }: Props) => {
  const [question, setQuestion] = useState<string>('')
  const [base64Image, setBase64Image] = useState<string | null>(null)
  const [base64PDF, setBase64PDF] = useState<string | null>(null)
  const [base64Docx, setBase64Docx] = useState<string | null>(null)
  const [base64Txt, setBase64Txt] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState<boolean>(false); // New state for upload status

  const [showExceededSizeDialog, setShowExceededSizeDialog] = useState(false);
  const [showFileTypeInvalidDialog, setShowFileTypeInvalidDialog] = useState(false);

  const handleOpenExceededSizeDialog = () => setShowExceededSizeDialog(true);
  const handleCloseExceededSizeDialog = () => setShowExceededSizeDialog(false);

  const handleOpenFileTypeInvalidDialog = () => setShowFileTypeInvalidDialog(true);
  const handleCloseFileTypeInvalidDialog = () => setShowFileTypeInvalidDialog(false);
  
  const appStateContext = useContext(AppStateContext)
  const OYD_ENABLED = appStateContext?.state.frontendSettings?.oyd_enabled || false

  // GET MAXIMUM FILE SIZE FROM COSMOS DB CONFIGURATION
  const maxSize = (appStateContext?.state.environmentVariablesList?.environment_variables?.max_upload_size || 5) ;


  const modalProps = {
    titleAriaId: 'labelId',
    subtitleAriaId: 'subTextId',
    isBlocking: true,
    styles: { main: { maxWidth: 450 } }
  }

  const errorExceededSizeDialogContentProps = {
    type: DialogType.close,
    title: "Error",
    closeButtonAriaLabel: 'Close',
    subText: `The file size exceeds ${maxSize} MB. Please upload a smaller file.`
  }

  const errorFileTypeInvalidDialogContentProps = {
    type: DialogType.close,
    title: "File Type Invalid",
    closeButtonAriaLabel: 'Close',
    subText: "Only PDF, DOCx, TXT and images are supported."
  }



  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (file && !(file.type.startsWith('image/') || file.type.startsWith('application/pdf') || file.type.startsWith('application/vnd.openxmlformats-officedocument.wordprocessingml.document') || file.type.startsWith('text/plain'))) {
      handleOpenFileTypeInvalidDialog()
      event.target.value = "" // Reset input value
      return
    }

    // Check file size (X MB = X * 1024 * 1024 bytes)
    if (file && file.size > (maxSize * 1024 * 1024)) {
      handleOpenExceededSizeDialog()
      event.target.value = "" // Reset input value
      return
    }

    setIsUploading(true)

    setBase64Image(null);
    setBase64PDF(null);
    setBase64Docx(null);
    setBase64Txt(null);

    if (file) {
      if (file.type.startsWith('image/')) {
        await convertToBase64Image(file)
      } else if (file.type.startsWith('application/pdf') || file.type.startsWith('application/vnd.openxmlformats-officedocument.wordprocessingml.document') || file.type.startsWith('text/plain')) {
        await convertToBase64File(file)
      } else {
        handleOpenFileTypeInvalidDialog()
      }
    }

    setIsUploading(false)
  }


  const convertToBase64Image = async (file: Blob) => {
    try {
      const resizedBase64 = await resizeImage(file, 800, 800)
      setBase64Image(resizedBase64)
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const convertToBase64File = async (file: Blob) => {
    if (file.type === 'application/pdf') {
      const reader = new FileReader()

      // Create a promise that resolves with the base64-encoded string
      const base64Pdf = new Promise<string>((resolve, reject) => {
        reader.onloadend = () => {
          const base64String = reader.result as string
          resolve(base64String)
        }

        reader.onerror = error => {
          reject(error)
        }

        // Read the file as a data URL (Base64-encoded string)
        reader.readAsDataURL(file)
      })

      const base64PdfString = await base64Pdf
      // Optionally set this string to a state or handle it further
      setBase64PDF(base64PdfString)
    } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      const reader = new FileReader()

      // Create a promise that resolves with the base64-encoded string
      const base64Pdf = new Promise<string>((resolve, reject) => {
        reader.onloadend = () => {
          const base64String = reader.result as string
          resolve(base64String)
        }

        reader.onerror = error => {
          reject(error)
        }

        // Read the file as a data URL (Base64-encoded string)
        reader.readAsDataURL(file)
      })

      const base64PdfString = await base64Pdf

      // Optionally set this string to a state or handle it further
      setBase64Docx(base64PdfString)
    } else if (file.type === 'text/plain') {
      const reader = new FileReader()

      // Create a promise that resolves with the base64-encoded string
      const base64Pdf = new Promise<string>((resolve, reject) => {
        reader.onloadend = () => {
          const base64String = reader.result as string
          resolve(base64String)
        }

        reader.onerror = error => {
          reject(error)
        }

        // Read the file as a data URL (Base64-encoded string)
        reader.readAsDataURL(file)
      })

      const base64PdfString = await base64Pdf

      // Optionally set this string to a state or handle it further
      setBase64Txt(base64PdfString)
    } else {
      console.error('The selected file is not an image, a PDF, a Docx or a TXT.')
    }
  }

  const sendQuestion = () => {
    if (disabled || !question.trim()) {
      return
    }

    const questionTest: ChatMessage['content'] = base64Image
      ? [
        { type: 'text', text: question },
        { type: 'image_url', image_url: { url: base64Image } }
      ]
      : question.toString()



    if (conversationId && questionTest !== undefined && (!base64PDF && !base64Docx && !base64Txt)) {
      onSend({ question: questionTest, id: conversationId });
      setBase64Image(null);
      setBase64PDF(null);
      setBase64Docx(null);
      setBase64Txt(null);
    } else if (conversationId && (base64PDF || base64Docx || base64Txt)) {

      // Determine which base64 variable is not null
      const context = base64PDF || base64Docx || base64Txt;

      onSend({ question: questionTest, context: context || undefined, id: conversationId });

      // Clear all base64 states
      setBase64Image(null);
      setBase64PDF(null);
      setBase64Docx(null);
      setBase64Txt(null);
    } else if (!conversationId && (base64PDF || base64Docx || base64Txt)) {

      // Determine which base64 variable is not null
      const context = base64PDF || base64Docx || base64Txt;

      onSend({ question: questionTest, context: context || undefined });

      // Clear all base64 states
      setBase64Image(null);
      setBase64PDF(null);
      setBase64Docx(null);
      setBase64Txt(null);
    }
    else {
      onSend({ question: questionTest });
      setBase64Image(null);
      setBase64PDF(null);
      setBase64Docx(null);
      setBase64Txt(null);
    }


    if (clearOnSend) {
      setQuestion('')
    }
  }

  const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
    if (ev.key === 'Enter' && !ev.shiftKey && !(ev.nativeEvent?.isComposing === true)) {
      ev.preventDefault()
      sendQuestion()
    }
  }

  const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setQuestion(newValue || '')
  }

  const sendQuestionDisabled = disabled || isUploading || !question.trim()

  const containerClass = messagesLength < 1 ? `${styles.questionInputContainer} ${styles.questionInputContainerEmpty}` : styles.questionInputContainer;


  return (
    <Stack horizontal className={containerClass}>
      <TextField
        className={styles.questionInputTextArea}
        placeholder={placeholder}
        multiline
        resizable={false}
        borderless
        value={question}
        onChange={onQuestionChange}
        onKeyDown={onEnterPress}
      />
      {OYD_ENABLED && (

        // PDF or Images - Working
        <div className={styles.fileInputContainer}>
          <input
            type="file"
            id="fileInput"
            onChange={event => handleFileUpload(event)}
            accept="image/*, application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document, text/plain"
            className={styles.fileInput}
          />
          <label htmlFor="fileInput" className={styles.fileLabel} aria-label="Upload File">
            <FontIcon className={styles.fileIcon} iconName={'Upload'} aria-label="Upload File" />
          </label>
        </div>
        /*<div className={styles.fileInputContainer}>
          <input
            type="file"
            id="fileInput"
            onChange={(event) => handleFileUpload(event)}
            accept="image/*"
            className={styles.fileInput}
          />
          <label htmlFor="fileInput" className={styles.fileLabel} aria-label='Upload Image'>
            <FontIcon
              className={styles.fileIcon}
              iconName={'PhotoCollection'}
              aria-label='Upload Image'
            />
          </label>
        </div>*/
      )
      }
      {base64Image && <img className={styles.uploadedImage} src={base64Image} alt="Uploaded Preview" />}

      {/* PDF TO DO */}
      {base64PDF && <iframe className={styles.uploadedPDF} src={base64PDF} title="PDF Preview" />}

      {base64Docx && <img src={DOCx} className={styles.uploadedDocx} aria-hidden="true" alt="DOCx" />}

      {base64Txt && <img src={TXT} className={styles.uploadedDocx} aria-hidden="true" alt="TXT" />}

      <div
        className={styles.questionInputSendButtonContainer}
        role="button"
        title="Send"
        tabIndex={0}
        aria-label="Ask question button"
        onClick={sendQuestion}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? sendQuestion() : null)}>
        {sendQuestionDisabled ? (
          <img src={Submit} className={styles.questionInputSendButtonDisabled} alt="Send Button" />
        ) : (
          <img src={Submit} className={styles.questionInputSendButton} alt="Send Button" />
        )}
      </div>
      {/* <div className={styles.questionInputBottomBorder} /> */}

      <Dialog
        hidden={!showExceededSizeDialog}
        onDismiss={handleCloseExceededSizeDialog}
        dialogContentProps={errorExceededSizeDialogContentProps}
        modalProps={modalProps}>
        <DialogFooter>
          <PrimaryButton onClick={handleCloseExceededSizeDialog} text="Exit" />
        </DialogFooter>
      </Dialog>

      <Dialog
        hidden={!showFileTypeInvalidDialog}
        onDismiss={handleCloseFileTypeInvalidDialog}
        dialogContentProps={errorFileTypeInvalidDialogContentProps}
        modalProps={modalProps}>
        <DialogFooter>
          <PrimaryButton onClick={handleCloseFileTypeInvalidDialog} text="Exit" />
        </DialogFooter>
      </Dialog>
    </Stack >

  )
}
